// strafe.games — credit / menu handle (plain DOM overlay, zero deps).
//
// CANONICAL COPY. Mirrored verbatim into every game's src/ (games share no
// build). Only edit the canonical copy here, then re-sync.
//
// The chip is an always-on HANDLE welded to the RIGHT WALL of the screen, near
// the top-right and pointing left: a small clean tab showing only a "‹" pull +
// the live credit count (no coin, no unit). Clicking it slides the menu in from
// the right (see platform-panel.ts). While the panel is open the handle hides —
// the panel renders its own matching handle on its left edge so the control
// reads as "the tab slid left with the drawer". There is no separate sign-in
// button: account lives inside the panel.
//
// Framework-agnostic — it injects ONE ref-counted <style> block whose every
// selector is `.strafe-plat-`-prefixed (no tag or global selectors), so it
// cannot collide with a game's own CSS.
//
// INPUT-SAFE: the root has `pointer-events: none`; only the handle re-enables
// pointer events, so the chip never steals canvas/game input. It never calls
// requestPointerLock and never binds WASD/global mouse.
//
// NEVER THROWS: every read of `platform.balance()` is guarded; on failure it
// shows the last-known value. Honours `prefers-reduced-motion`. Returns a no-op
// handle when `document` is absent (SSR / headless / the solver harness).

import type { Platform as PlatformType } from './platform';
import { openPlatformPanel, type PanelHandle, type SectionId } from './platform-panel';

type Platform = typeof PlatformType;

export type Provider = 'discord' | 'google' | 'github' | 'password';

export interface AuthSessionLike {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
}

/** Result of an email/password attempt. Mirrors `auth.ts`'s `AuthResult`. */
export type AuthResultLike = { ok: true } | { ok: false; error: string };

/** The minimal slice of the auth client the chip needs. Kept structural so any
 *  auth module (the canonical `auth.ts`, or a game-specific one) satisfies it.
 *  Account UI lives in the panel now — the chip only forwards `auth` along. */
export interface AuthClientLike {
  isAuthEnabled(): boolean;
  getSession(): Promise<AuthSessionLike | null>;
  signIn(provider: Provider): void;
  signOut(): Promise<void> | void;
  subscribe(fn: (s: AuthSessionLike | null) => void): () => void;
  login?(email: string, password: string): Promise<AuthResultLike>;
  register?(email: string, password: string, turnstileToken?: string): Promise<AuthResultLike>;
}

type Corner = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface MountChipOptions {
  platform: Platform;
  auth?: AuthClientLike | null;
  container?: HTMLElement;
  /** Accepted for backwards-compat; the handle always docks to the right wall. */
  corner?: Corner;
  unit?: string;
  inset?: number;
}

export interface ChipHandle {
  refresh(): void;
  destroy(): void;
  el: HTMLElement;
}

const STYLE_ID = 'strafe-plat-styles';

// ---- ref-counted single <style> ----------------------------------------------

let styleRefCount = 0;

function ensureStyle(): void {
  if (typeof document === 'undefined') return;
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function releaseStyle(): void {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount === 0) {
    const el = typeof document !== 'undefined' ? document.getElementById(STYLE_ID) : null;
    if (el) el.remove();
  }
}

// Palette mirrors the hub: #FF4500 orange accents on off-white "ink" (#f2efe8),
// cool near-black surfaces (#07080a / #0b0d11). No gold.
const CSS = `
.strafe-plat-root{position:fixed;z-index:2147483000;pointer-events:none;
  top:calc(env(safe-area-inset-top, 0px) + 64px);right:0;
  font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
  -webkit-font-smoothing:antialiased;}
.strafe-plat-root[data-open="1"]{display:none;}
.strafe-plat-handle{pointer-events:auto;display:inline-flex;align-items:center;gap:8px;
  height:44px;padding:0 14px;border-radius:14px 0 0 14px;
  border:1px solid rgba(255,69,0,.46);border-right:0;
  background:linear-gradient(180deg,rgba(17,15,13,.92),rgba(8,8,9,.95));
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  box-shadow:-7px 0 24px rgba(0,0,0,.46),inset 0 1px 0 rgba(255,179,138,.22);
  color:#f2efe8;cursor:pointer;font-family:inherit;opacity:1;
  transition:transform .18s cubic-bezier(.16,1,.3,1),border-color .16s ease,box-shadow .16s ease,opacity .2s ease;}
.strafe-plat-handle:hover{transform:translateX(-4px);border-color:rgba(255,69,0,.85);
  box-shadow:-9px 0 28px rgba(0,0,0,.52),inset 0 1px 0 rgba(255,179,138,.36);}
.strafe-plat-handle:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}
/* Idle-subtle on hover-capable devices: the handle fades back so it never
   blocks a game's HUD, and returns to full opacity when the cursor is OVER or
   NEAR it (JS data-near), on focus, or briefly when the balance changes
   (data-flash). Touch devices (no hover) keep it fully visible to tap. */
@media (hover:hover){
  .strafe-plat-handle{opacity:.32;}
  .strafe-plat-root[data-near="1"] .strafe-plat-handle,
  .strafe-plat-root[data-flash="1"] .strafe-plat-handle,
  .strafe-plat-handle:hover,
  .strafe-plat-handle:focus-visible{opacity:1;}
}
.strafe-plat-chev{font-size:14px;line-height:1;color:#ff7a3c;font-weight:800;
  transition:transform .18s cubic-bezier(.16,1,.3,1);}
.strafe-plat-handle:hover .strafe-plat-chev{transform:translateX(-3px);}
.strafe-plat-amount{position:relative;font-size:16px;font-weight:800;color:#f2efe8;
  font-variant-numeric:tabular-nums;letter-spacing:.01em;white-space:nowrap;line-height:1;}
.strafe-plat-amount[data-bump="1"]{animation:strafe-plat-bump .34s ease;}
.strafe-plat-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;
  overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
@keyframes strafe-plat-bump{0%{transform:scale(1)}40%{transform:scale(1.16)}100%{transform:scale(1)}}
@media (prefers-reduced-motion:reduce){
  .strafe-plat-handle,.strafe-plat-chev{transition:none;}
  .strafe-plat-handle:hover{transform:none;}
  .strafe-plat-handle:hover .strafe-plat-chev{transform:none;}
  .strafe-plat-amount[data-bump="1"]{animation:none;}
}
`;

// ---- mount -------------------------------------------------------------------

const NOOP_HANDLE: ChipHandle = {
  refresh() {},
  destroy() {},
  el: (typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement)),
};

function prefersReducedMotion(): boolean {
  return safe(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion:reduce)').matches,
    false,
  );
}

export function mountPlatformChip(opts: MountChipOptions): ChipHandle {
  if (typeof document === 'undefined') return NOOP_HANDLE;

  const { platform } = opts;
  const auth = opts.auth ?? null;
  const container = opts.container ?? document.body;

  ensureStyle();

  // ---- root: docked to the right wall, vertically centred ----
  const root = document.createElement('div');
  root.className = 'strafe-plat-root';
  // Respect a notch / rounded corner on the right.
  root.style.right = `env(safe-area-inset-right, 0px)`;

  // ---- the handle (coin + count + pull) ----
  const handle = document.createElement('button');
  handle.type = 'button';
  handle.className = 'strafe-plat-handle';

  const chev = document.createElement('span');
  chev.className = 'strafe-plat-chev';
  chev.setAttribute('aria-hidden', 'true');
  chev.textContent = '‹';

  const amount = document.createElement('span');
  amount.className = 'strafe-plat-amount';
  const amountText = document.createTextNode('0');
  amount.append(amountText);

  handle.append(chev, amount);

  const status = document.createElement('span');
  status.className = 'strafe-plat-sr';
  status.setAttribute('aria-live', 'polite');

  root.append(handle, status);

  // ---- render: balance ----
  let lastBalance = -1;

  function render(): void {
    const bal = safe(() => platform.balance(), lastBalance < 0 ? 0 : lastBalance);
    amountText.nodeValue = String(bal);
    handle.setAttribute('aria-label', `Open menu — ${bal} credits`);

    if (bal !== lastBalance && lastBalance >= 0) {
      status.textContent = `${bal} credits`;
      // Briefly surface the (idle-faint) handle so an earn/spend is noticed.
      root.setAttribute('data-flash', '1');
      window.setTimeout(() => root.removeAttribute('data-flash'), 1400);
      if (!prefersReducedMotion()) {
        amount.setAttribute('data-bump', '1');
        window.setTimeout(() => amount.removeAttribute('data-bump'), 360);
      }
    }
    lastBalance = bal;
  }

  // ---- panel toggle ----
  let panel: PanelHandle | null = null;

  function syncOpenState(): void {
    const open = !!(panel && safe(() => panel!.isOpen(), false));
    if (open) {
      root.setAttribute('data-open', '1');
      handle.setAttribute('aria-expanded', 'true');
    } else {
      root.removeAttribute('data-open');
      handle.setAttribute('aria-expanded', 'false');
    }
  }
  handle.setAttribute('aria-expanded', 'false');

  function togglePanel(section?: SectionId): void {
    if (panel && safe(() => panel!.isOpen(), false)) {
      safe(() => panel!.close(), undefined);
      panel = null;
      syncOpenState();
      return;
    }
    panel = safe(
      () => openPlatformPanel({ platform, auth, section, onClose: syncOpenState }),
      null,
    );
    syncOpenState();
  }

  handle.addEventListener('click', () => togglePanel());

  // ---- wire ----
  container.append(root);
  render();

  // platform balance subscription
  const offPlatform = safe(() => platform.onChange(render), () => {});

  // Reveal-on-approach: while idle the handle is faint (CSS, hover-capable
  // devices only); when the cursor comes near it we surface it. The listener is
  // PASSIVE and the root keeps pointer-events:none, so it never steals game
  // input (it only reads the cursor position and toggles an attribute).
  let nearRaf = 0;
  const REVEAL_PX = 150;
  const onMove = (e: MouseEvent): void => {
    if (nearRaf) return;
    const cx = e.clientX;
    const cy = e.clientY;
    nearRaf = window.requestAnimationFrame(() => {
      nearRaf = 0;
      const r = handle.getBoundingClientRect();
      const dx = cx < r.left ? r.left - cx : cx > r.right ? cx - r.right : 0;
      const dy = cy < r.top ? r.top - cy : cy > r.bottom ? cy - r.bottom : 0;
      if (dx * dx + dy * dy <= REVEAL_PX * REVEAL_PX) root.setAttribute('data-near', '1');
      else root.removeAttribute('data-near');
    });
  };
  window.addEventListener('mousemove', onMove, { passive: true });

  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') render();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    refresh: render,
    el: root,
    destroy(): void {
      offPlatform();
      window.removeEventListener('mousemove', onMove);
      if (nearRaf) window.cancelAnimationFrame(nearRaf);
      document.removeEventListener('visibilitychange', onVisibility);
      if (panel) safe(() => panel!.close(), undefined);
      root.remove();
      releaseStyle();
    },
  };
}

// ---- guards ------------------------------------------------------------------

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
